import {
  postDailyAnalytics,
  postDailyDetailedAnalytics,
  postReferralDailyAnalytics,
  TPostDailyAnalyticsRequest,
  TPostDailyDetailedAnalyticsRequest,
  TPostReferralDailyAnalyticsRequest,
} from "api/clinicos";
import {
  DeviceType,
  ProcedureType,
  Session,
  TCTACopy,
  TDailyAnalyticsCount,
  TFormName,
  TFormVersion,
} from "api/type";
import { getUrlParam } from "./getUrlParam";

const callPostDailyAnalytics = async (
  session: Session,
  formName: TFormName,
  dailyAnalyticsCount: TDailyAnalyticsCount,
) => {
  try {
    const body: TPostDailyAnalyticsRequest = {
      form_name: formName,
      daily_analytics_count_type: dailyAnalyticsCount,
    };
    await postDailyAnalytics(session, body);
  } catch (error) {
    console.error("Error calling POST /daily-analytics", error);
  }
};

const callPostDailyDetailedAnalytics = async (
  session: Session,
  formName: TFormName,
  ctaCopy: TCTACopy,
  formVersion: TFormVersion,
  deviceType: DeviceType,
  dailyAnalyticsCount: TDailyAnalyticsCount,
) => {
  try {
    const body: TPostDailyDetailedAnalyticsRequest = {
      form_name: formName,
      cta_copy: ctaCopy,
      form_version: formVersion,
      device_type: deviceType,
      daily_analytics_count_type: dailyAnalyticsCount,
    };
    await postDailyDetailedAnalytics(session, body);
  } catch (error) {
    console.error("Error calling POST /daily-analytics", error);
  }
};

const callPostReferralDailyAnalytics = async (
  session: Session,
  formName: TFormName,
  referrer: string,
) => {
  try {
    const body: TPostReferralDailyAnalyticsRequest = {
      form_name: formName,
      referrer: referrer,
    };
    await postReferralDailyAnalytics(session, body);
  } catch (error) {
    console.error("Error calling POST /referral-daily-analytics", error);
  }
};

export const callSiteVisitTracking = async (
  session: Session | null,
  formName: TFormName = TFormName.ONBOARDING_TIER1,
) => {
  if (!session) return;
  const hasCalledDailyAnalyticsSiteVisit = localStorage.getItem(
    "hasCalledDailyAnalyticsSiteVisit",
  );

  if (!hasCalledDailyAnalyticsSiteVisit) {
    callPostDailyAnalytics(session, formName, TDailyAnalyticsCount.SITE_VISITS);
    localStorage.setItem("hasCalledDailyAnalyticsSiteVisit", "true");
  }
};

export const callFormVisitTracking = async (
  session: Session | null,
  formName: TFormName = TFormName.ONBOARDING_TIER1,
) => {
  if (!session) return;
  const hasCalledDailyAnalyticsFormVisits = localStorage.getItem(
    "hasCalledDailyAnalyticsFormVisits",
  );

  if (!hasCalledDailyAnalyticsFormVisits) {
    callPostDailyAnalytics(session, formName, TDailyAnalyticsCount.FORM_VISITS);
    localStorage.setItem("hasCalledDailyAnalyticsFormVisits", "true");
  }
};

export const callFormSubmissionTracking = async (
  session: Session | null,
  formName: TFormName = TFormName.ONBOARDING_TIER1,
) => {
  if (!session) return;
  const hasCalledDailyAnalyticsFormSubmission = localStorage.getItem(
    "hasCalledDailyAnalyticsFormSubmission",
  );

  if (!hasCalledDailyAnalyticsFormSubmission) {
    callPostDailyAnalytics(
      session,
      formName,
      TDailyAnalyticsCount.FORM_SUBMISSIONS,
    );

    localStorage.setItem("hasCalledDailyAnalyticsFormSubmission", "true");
  }
};

export const callFormSimulationTracking = async (
  session: Session | null,
  formName: TFormName = TFormName.ONBOARDING_TIER1,
) => {
  if (!session) return;
  const hasCalledDailyAnalyticsFormSimulation = localStorage.getItem(
    "hasCalledDailyAnalyticsFormSimulation",
  );
  if (!hasCalledDailyAnalyticsFormSimulation) {
    callPostDailyAnalytics(
      session,
      formName,
      TDailyAnalyticsCount.FORM_SIMULATIONS,
    );
    localStorage.setItem("hasCalledDailyAnalyticsFormSimulation", "true");
  }
};

export const callReferralTracking = async (
  session: Session | null,
  formName: TFormName = TFormName.ONBOARDING_TIER1,
) => {
  if (!session) return;
  const hasCalledReferralDailyAnalytics = sessionStorage.getItem(
    "hasCalledReferralDailyAnalytics",
  );

  // Only call callPostReferralDailyAnalytics if it hasn't been called yet
  if (!hasCalledReferralDailyAnalytics) {
    const currentUrl = window.location.search;
    const urlSearchParams = new URLSearchParams(currentUrl);
    const referrer = urlSearchParams.get("referrer") || "direct";
    callPostReferralDailyAnalytics(session, formName, referrer);
    sessionStorage.setItem("hasCalledReferralDailyAnalytics", "true");
  }
};

export const callDetailedSiteVisitTracking = async (
  session: Session | null,
  formName: TFormName = TFormName.ONBOARDING_TIER1,
  ctaCopy: TCTACopy,
  formVersion: TFormVersion,
  deviceType: DeviceType,
) => {
  if (!session) return;
  const hasCalledDetailedDailyAnalyticsSiteVisit = sessionStorage.getItem(
    "hasCalledDetailedDailyAnalyticsSiteVisit",
  );

  if (!hasCalledDetailedDailyAnalyticsSiteVisit) {
    callPostDailyDetailedAnalytics(
      session,
      formName,
      ctaCopy,
      formVersion,
      deviceType,
      TDailyAnalyticsCount.SITE_VISITS,
    );
    sessionStorage.setItem("hasCalledDetailedDailyAnalyticsSiteVisit", "true");
  }
};

export const callDetailedFormVisitTracking = async (
  session: Session | null,
  formName: TFormName = TFormName.ONBOARDING_TIER1,
  ctaCopy: TCTACopy,
  formVersion: TFormVersion,
  deviceType: DeviceType,
) => {
  if (!session) return;
  const hasCalledDetailedDailyAnalyticsFormVisits =
    sessionStorage.getItem(ctaCopy);

  if (!hasCalledDetailedDailyAnalyticsFormVisits) {
    callPostDailyDetailedAnalytics(
      session,
      formName,
      ctaCopy,
      formVersion,
      deviceType,
      TDailyAnalyticsCount.FORM_VISITS,
    );
    sessionStorage.setItem(ctaCopy, "true");
  }
};

export const callDetailedFormSubmissionTracking = async (
  session: Session | null,
  formName: TFormName = TFormName.ONBOARDING_TIER1,
  ctaCopy: TCTACopy,
  formVersion: TFormVersion,
  deviceType: DeviceType,
) => {
  if (!session) return;
  const hasCalledDetailedDailyAnalyticsFormSubmission = sessionStorage.getItem(
    "hasCalledDetailedDailyAnalyticsFormSubmission",
  );

  if (!hasCalledDetailedDailyAnalyticsFormSubmission) {
    callPostDailyDetailedAnalytics(
      session,
      formName,
      ctaCopy,
      formVersion,
      deviceType,
      TDailyAnalyticsCount.FORM_SUBMISSIONS,
    );

    sessionStorage.setItem(
      "hasCalledDetailedDailyAnalyticsFormSubmission",
      "true",
    );
  }
};

export const callDetailedFormSimulationTracking = async (
  session: Session | null,
  formName: TFormName = TFormName.ONBOARDING_TIER1,
  ctaCopy: TCTACopy,
  formVersion: TFormVersion,
  deviceType: DeviceType,
) => {
  if (!session) return;
  const hasCalledDetailedDailyAnalyticsFormSimulation = sessionStorage.getItem(
    "hasCalledDetailedDailyAnalyticsFormSimulation",
  );
  if (!hasCalledDetailedDailyAnalyticsFormSimulation) {
    callPostDailyDetailedAnalytics(
      session,
      formName,
      ctaCopy,
      formVersion,
      deviceType,
      TDailyAnalyticsCount.FORM_SIMULATIONS,
    );
    sessionStorage.setItem(
      "hasCalledDetailedDailyAnalyticsFormSimulation",
      "true",
    );
  }
};

// TODO: This function is meant to be deleted when the feature is no longer needed
export const callCTATracking = (
  session: Session | null,
  contextProcedure: ProcedureType | null,
) => {
  if (!session) return;
  const formVersion = getUrlParam("formVersion") as TFormVersion;

  const isMobile = getUrlParam("isMobile");

  let deviceType: DeviceType | null = null;
  if (isMobile === "true") {
    deviceType = DeviceType.MOBILE;
  } else if (isMobile === "false") {
    deviceType = DeviceType.DESKTOP;
  } else {
    deviceType = null;
  }

  const ctaCopyNumber = getUrlParam("ctaCopy");
  let ctaCopy: TCTACopy | null = null;
  if (ctaCopyNumber === "1") {
    ctaCopy = TCTACopy.SEE_HOW_YOU_WILL_LOOK_WITH_A_X;
  } else if (ctaCopyNumber === "2") {
  } else if (ctaCopyNumber === "3") {
    ctaCopy = TCTACopy.SHOW_UP_PREPARED_VISUALIZE_YOUR_X_NOW;
  } else if (ctaCopyNumber === "4") {
    ctaCopy = TCTACopy.SIMULATE_YOUR_NEXT_X;
  } else if (ctaCopyNumber === "5") {
    ctaCopy = TCTACopy.VISUALIZE_YOUR_X_IN_TWO_MINUTES;
  } else if (ctaCopyNumber === "6") {
    ctaCopy = TCTACopy.VISUALIZE_YOUR_X_IN_TWO_MINUTES_BLUE;
  } else if (ctaCopyNumber === "7") {
    ctaCopy = TCTACopy.VISUALIZE_YOUR_X_INTANTLY;
  } else {
    ctaCopy = null;
  }
  if (!contextProcedure) {
    ctaCopy = TCTACopy.DEFAULT_COPY;
  }

  if (!ctaCopy || !deviceType || !formVersion) {
    return;
  }
  callDetailedFormVisitTracking(
    session,
    TFormName.ONBOARDING_TIER1,
    ctaCopy,
    formVersion,
    deviceType,
  );
};
